import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  memo,
  useRef,
} from "react";
import { useDataManagement } from "../ferramentas/RequisitarAPI.ts";
import style from "./ModalStyles.module.css";
import makeAnimated from "react-select/animated";
import Placeholder from "../ferramentas/placeholder/Placeholder.js";
import ToastMessage from "../ferramentas/toastMessage/ToastMessage";
import MultiDataPicker from "../ferramentas/multiDataPicker/MultiDataPicker.js";

// ===================================================================
// ===== FUNÇÃO AUXILIAR PARA AJUSTE DE FUSO HORÁRIO (GAMBIARRA) =====
// ===================================================================
// Quando o backend for corrigido, poderá simplesmente fazer
// esta função retornar a data sem modificação, ou remover seu uso.
const adjustDateForBackend = (date) => {
  const temporaryDate = new Date(date);

  // 1. Subtrai 3 horas
  temporaryDate.setHours(temporaryDate.getHours() - 3);

  // 2. Formata para string ISO e remove os milissegundos e o 'Z'
  // Exemplo de saída: "2023-10-27T10:30:00"
  return temporaryDate.toISOString().split(".")[0];
};
// ===================================================================

export const AgendamentoModal = memo(
  ({ onClose, onSave, onDelete, selectedDate, selectedEvent, onBulkEdit, isBulk, prefillData }) => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");

    const [formData, setFormData] = useState({
      id: null,
      title: "",
      description: "",
      customer: "",
      initial_time: "",
      initial_time2: `${hours}:${minutes}`,
      services: null,
      user: null,
    });

    const [options, setOptions] = useState([]);
    const [optionsUsers, setOptionsUsers] = useState([]);
    const { users, services, appointments, error, load } = useDataManagement();
    const animatedComponents = useMemo(() => makeAnimated(), []);
    const [selectedDatesFromPicker, setSelectedDatesFromPicker] = useState(
      selectedDate || [new Date()],
    );
    const titleInputRef = useRef(null);
    const [bulkAppointments, setBulkAppointments] = useState([]);

    // useEffect para foco
    useEffect(() => {
      setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
        }
      }, 100);
    }, []);

    // useEffect para tecla Escape
    useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [onClose]);

    useEffect(() => {
      const fetchBulkAppointments = async () => {
        if (selectedEvent?.event?.extendedProps?.full_appointment?.group) {
          const groupId =
            selectedEvent.event.extendedProps.full_appointment.group;
          try {
            const result = await appointments.getBulk(groupId);
            if (result.ok) {
              const appointmentsArray = Object.values(result.data);
              setBulkAppointments(appointmentsArray);
            } else {
            }
          } catch (error) { }
        } else {
          setBulkAppointments([]); // Limpa o estado se não houver grupo
        }
      };

      fetchBulkAppointments();
    }, [selectedEvent, appointments]);

    const loadServices = useCallback(async () => {
      try {
        const result = await services.list();
        if (result.aborted) return;
        if (result.ok) {
          const formattedOptions = Object.keys(result.data).map((key) => ({
            value: result.data[key],
            label: `${result.data[key].name
              } (${result.data[key].time_service.slice(0, 5)})`,
          }));
          setOptions(formattedOptions);
        } else if (error) {
          console.error("Erro ao carregar serviços:", error);
        }
      } catch (err) {
        console.error("Erro ao carregar serviços:", err);
      }
    }, [services, error]);
    const loadUsers = useCallback(async () => {
      try {
        const result = await users.list();
        if (result.aborted) return;
        if (result.ok) {
          const formattedOptions = Object.keys(result.data).map((key) => ({
            value: result.data[key],
            label: result.data[key].fullname,
          }));
          setOptionsUsers(formattedOptions);
        } else if (error) {
          console.error("Erro ao carregar usuários:", error);
        }
      } catch (err) {
        console.error("Erro ao carregar usuários:", err);
      }
    }, [users, error]);
    useEffect(() => {
      if (options.length === 0) loadServices();
      if (optionsUsers.length === 0) loadUsers();
    }, [loadServices, loadUsers, options.length, optionsUsers.length]);

    // Efeito para pré-preenchimento com dados vindos da URL (prefillData)
    useEffect(() => {
      if (prefillData && options.length > 0 && optionsUsers.length > 0) {
        // Encontra o serviço pelo ID

        // Encontra o usuário pelo ID
        const userOption = optionsUsers.find(opt => opt.value.id === prefillData.userId);

        // Lógica para encontrar serviços (suporta múltiplos ou único)
        let selectedServices = null;
        if (prefillData.serviceIds && Array.isArray(prefillData.serviceIds)) {
          // Se vier lista de IDs (novo padrão)
          const foundServices = options.filter(opt => prefillData.serviceIds.includes(opt.value.id));
          if (foundServices.length > 0) selectedServices = foundServices;
        } else if (prefillData.serviceId) {
          // Fallback para único ID (antigo)
          const serviceOption = options.find(opt => opt.value.id === prefillData.serviceId);
          if (serviceOption) selectedServices = [serviceOption];
        }

        // Extrai hora do date string
        const prefillDate = new Date(prefillData.date);
        const prefillHours = String(prefillDate.getHours()).padStart(2, '0');
        const prefillMinutes = String(prefillDate.getMinutes()).padStart(2, '0');

        setFormData(prev => ({
          ...prev,
          title: prefillData.serviceName || '',
          customer: prefillData.customer || '',
          initial_time2: `${prefillHours}:${prefillMinutes}`,
          services: selectedServices,
          user: userOption || null,
        }));
      }
    }, [prefillData, options, optionsUsers]);

    useEffect(() => {
      if (selectedEvent) {
        try {
          const listaServicos = Object.entries(
            selectedEvent.event.extendedProps.services
          ).map(([id, servico]) => ({
            value: servico,
            label: `${servico.name} (${servico.time_service.slice(0, 5)})`,
          }));

          // --- CORREÇÃO AQUI ---
          // Formatando corretamente horas e minutos com zero à esquerda
          const eventDate = selectedEvent.event.start;
          const formattedHours = String(eventDate.getHours()).padStart(2, '0');
          const formattedMinutes = String(eventDate.getMinutes()).padStart(2, '0');

          setFormData({
            id: parseInt(selectedEvent.event.id),
            title: selectedEvent.event.title,
            initial_time: selectedEvent.event.start.toISOString(),
            initial_time2: `${formattedHours}:${formattedMinutes}`, // Usando a versão formatada
            description: selectedEvent.event.extendedProps.description,
            services: listaServicos,
            customer: selectedEvent.event.extendedProps.customer,
            user: {
              value: selectedEvent.event.extendedProps.user,
              label: selectedEvent.event.extendedProps.user.fullname,
            },
          });
          if (
            selectedEvent.event.extendedProps.allDates &&
            Array.isArray(selectedEvent.event.extendedProps.allDates)
          ) {
            setSelectedDatesFromPicker(
              selectedEvent.event.extendedProps.allDates.map(
                (dateStr) => new Date(dateStr)
              )
            );
          } else {
            setSelectedDatesFromPicker([new Date(selectedEvent.event.start)]);
          }
        } catch (error) {
          console.error("Erro ao processar evento selecionado:", error);
          setSelectedDatesFromPicker(
            selectedEvent.event.start
              ? [new Date(selectedEvent.event.start)]
              : []
          );
        }
      } else {
        const initialDateTime = selectedDate[0].toISOString();
        setFormData((prevFormData) => ({
          ...prevFormData,
          id: null,
          title: "",
          description: "",
          customer: "",
          services: null,
          user: null,
          initial_time: initialDateTime,
        }));
        setSelectedDatesFromPicker(selectedDate);
      }
    }, [selectedEvent, selectedDate]);

    const handleInputChange = useCallback((e) => {
      const { name, value } = e.target;
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }, []);

    const handleDatesChangeFromPicker = useCallback((dates) => {
      setSelectedDatesFromPicker(
        dates.sort((a, b) => a.getTime() - b.getTime())
      );
    }, []);

    const handleSubmit = useCallback(
      (e) => {
        e.preventDefault();
        const { title, customer, initial_time2, services, user } = formData;
        let cancelarAgendamento = {
          dataPassada: false,
          semHorarioInicial: false,
        };

        if (!title || !customer || !services || !user) {
          ToastMessage({
            cod: "e",
            texto:
              "Por favor, preencha Título, Cliente, Serviço e Funcionário.",
          });
          return;
        }
        if (selectedDatesFromPicker.length === 0) {
          ToastMessage({
            cod: "e",
            texto:
              "Por favor, selecione pelo menos uma data para o agendamento.",
          });
          return;
        }

        let finalDatesToSend = selectedDatesFromPicker
          .map((date) => {
            const baseDate = new Date(date);
            const dataHoje = new Date();

            if (initial_time2) {
              const [hours, minutes] = initial_time2.split(":").map(Number);
              baseDate.setHours(hours, minutes, 0, 0);

              if (baseDate < dataHoje) {
                cancelarAgendamento.dataPassada = true;
                return null;
              }

              const adjustedDateString = adjustDateForBackend(baseDate);
              return { initial_time: adjustedDateString };
            } else {
              cancelarAgendamento.semHorarioInicial = true;
              return null;
            }
          })
          .filter(Boolean);

        if (cancelarAgendamento.dataPassada) {
          ToastMessage({
            cod: "e",
            texto:
              "Não é possível agendar compromissos para datas e horários passados.",
          });
          return;
        }
        if (cancelarAgendamento.semHorarioInicial) {
          ToastMessage({
            cod: "e",
            texto: "Por favor, defina um horário inicial para o agendamento.",
          });
          return;
        }
        if (finalDatesToSend.length === 0) return;

        onSave({
          id: formData.id,
          title: formData.title,
          description: formData.description,
          customer: formData.customer,
          initial_time: finalDatesToSend[0].initial_time,
          user: user.value.id,
          services: services.map((service) => ({ id: service.value.id })),
          user_obj: user.value,
          appointments: finalDatesToSend,
          initial_time2: formData.initial_time2,
        });
        onClose("esperar");
      },
      [formData, onSave, selectedDatesFromPicker, onClose]
    );

    const handleDelete = useCallback(() => {
      if (selectedEvent) onDelete(selectedEvent.event.id);
    }, [selectedEvent, onDelete]);

    const handleBulkEditClick = useCallback(() => {
      onBulkEdit();
      onClose();
    }, [onBulkEdit, onClose]);

    const modalContent = useMemo(
      () => (
        <div className={style.modal_content}>
          <div className={style.modalHeader}>
            <h2>
              {isBulk ? "Editar Agendamentos em Lote" : selectedEvent ? "Editar Compromisso" : "Agendar Compromisso"}
            </h2>
            <span className={style.close} onClick={onClose}>
              ×
            </span>
          </div>

          <div className={style.modalBody}>
            <form
              id="agendamentoForm"
              className={style.modalForm}
              onSubmit={handleSubmit}
            >
              <div className={style.fullWidth}>
                <Placeholder
                  ref={titleInputRef}
                  label="Título do Compromisso"
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className={style.fullWidth}>
                <MultiDataPicker
                  initialSelectedDates={selectedDatesFromPicker}
                  onDatesChange={handleDatesChangeFromPicker}
                />
              </div>

              <Placeholder
                label="Cliente"
                type="text"
                name="customer"
                value={formData.customer}
                onChange={handleInputChange}
                required
              />

              <Placeholder
                label="Horário Inicial"
                type="time"
                name="initial_time2"
                value={formData.initial_time2}
                onChange={handleInputChange}
                required
              />

              <div className={`${style.fullWidth} ${style.select}`}>
                <Placeholder
                  label="Funcionário"
                  type="select"
                  name="user"
                  classNamePrefix="user"
                  components={animatedComponents}
                  value={formData.user}
                  onChange={(selectedUser) =>
                    handleInputChange({
                      target: { value: selectedUser, name: "user" },
                    })
                  }
                  options={optionsUsers}
                  required
                  isLoading={load.isLoading}
                />
              </div>

              <div className={`${style.fullWidth} ${style.select}`}>
                <Placeholder
                  label="Serviço(s)"
                  type="select"
                  name="services"
                  classNamePrefix="services"
                  components={animatedComponents}
                  value={formData.services}
                  onChange={(selectedOption) =>
                    handleInputChange({
                      target: { value: selectedOption, name: "services" },
                    })
                  }
                  options={options}
                  isMulti
                  required
                  isLoading={load.isLoading}
                />
              </div>

              <div className={style.fullWidth}>
                <Placeholder
                  label="Descrição (Opcional)"
                  type="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>
            </form>
          </div>

          <div className={style.modalFooter}>
            <button
              className={`${style.modalButton} ${style.saveButton}`}
              type="submit"
              form="agendamentoForm"
              disabled={load.isLoading}
            >
              {load.isLoading
                ? "Salvando..."
                : isBulk
                  ? "Salvar Alterações em Lote"
                  : selectedEvent
                    ? "Salvar Alterações"
                    : "Salvar Compromisso"}
            </button>
            {bulkAppointments && bulkAppointments.length > 1 && (
              <button
                className={`${style.modalButton} ${style.saveButton}`}
                type="button"
                onClick={handleBulkEditClick}
                disabled={load.isLoading}
              >
                Gerenciar Recorrências
              </button>
            )}
            {selectedEvent && (
              <button
                className={`${style.modalButton} ${style.deleteButton}`}
                type="button"
                onClick={handleDelete}
                disabled={load.isLoading}
              >
                Excluir
              </button>
            )}
          </div>
        </div>
      ),
      [
        formData,
        handleInputChange,
        handleSubmit,
        handleDelete,
        selectedEvent,
        onClose,
        options,
        optionsUsers,
        animatedComponents,
        selectedDatesFromPicker,
        handleDatesChangeFromPicker,
        load,
        bulkAppointments,
        handleBulkEditClick,
        isBulk,
      ]
    );

    return (
      <>
        <div
          className={style.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          {modalContent}
        </div>
      </>
    );
  }
);